import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PenTool, CalendarCheck, QrCode, MapPin, Camera, Play } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./Reveal";

function DemoLink({ to, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full bg-terracotta/15 px-3 py-1.5 text-xs font-medium text-terracotta transition-colors duration-300 hover:bg-terracotta hover:text-[#1c0d08]"
    >
      <Play size={11} fill="currentColor" /> Prova la demo
    </Link>
  );
}

const cardBase =
  "group relative overflow-hidden rounded-3xl border border-line bg-panel p-7 transition-colors duration-500 hover:border-terracotta/50 lg:p-8";

function Card({ children, className = "", delay = 0, testid }) {
  return (
    <motion.article
      data-testid={testid}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.85, delay, ease: EASE }}
      className={`${cardBase} ${className}`}
    >
      {children}
    </motion.article>
  );
}

function IconBox({ icon: Icon }) {
  return (
    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-line bg-ink text-terracotta transition-colors duration-500 group-hover:border-terracotta/40">
      <Icon size={19} strokeWidth={1.8} />
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="rounded-full border border-line px-3 py-1.5 text-xs text-stone transition-colors duration-500 group-hover:border-terracotta/30">
      {children}
    </span>
  );
}

function Head({ n, icon, title, desc }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="font-accent text-[11px] tracking-[0.3em] text-faded">{n}</p>
        <h3 className="mt-3 font-serif text-2xl tracking-tight text-bone">{title}</h3>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-stone">{desc}</p>
      </div>
      <IconBox icon={icon} />
    </div>
  );
}

export default function Services() {
  return (
    <section id="servizi" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          tag="Servizi"
          title={
            <>
              Tutto ciò che serve per farti trovare — <em className="italic text-terracotta">e scegliere.</em>
            </>
          }
          sub="Non vendiamo siti: costruiamo il sistema che trasforma chi ti cerca in chi ti sceglie, tavolo dopo tavolo, camera dopo camera."
        />

        <div className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-12">
          {/* Big card — mini browser mock */}
          <Card className="lg:col-span-7" delay={0} testid="service-card-vetrina">
            <Head
              n="01"
              icon={PenTool}
              title="Vetrina Web Sartoriale"
              desc="Siti ultra-veloci, pensati per convertire i visitatori in clienti reali. Ogni progetto nasce da zero: niente template pre-fatti, solo il tuo carattere."
            />
            <div className="mt-8 overflow-hidden rounded-2xl border border-line bg-ink">
              <div className="flex items-center gap-2 border-b border-line px-4 py-3">
                <span className="h-2.5 w-2.5 rounded-full bg-terracotta/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-ochre/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-cypress/80" />
                <span className="ml-3 rounded-full border border-line px-3 py-1 font-accent text-[10px] tracking-[0.14em] text-faded">
                  tualocale.it
                </span>
              </div>
              <div className="p-5">
                <div className="flex h-24 items-center justify-center rounded-xl border border-line bg-gradient-to-br from-panel-2 to-panel">
                  <span className="font-serif text-lg italic text-stone">La tua home page</span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="h-9 rounded-lg border border-line bg-panel-2" />
                  <div className="h-9 rounded-lg border border-line bg-panel-2" />
                  <div className="h-9 rounded-lg border border-line bg-panel-2" />
                </div>
                <div className="mt-4 flex items-center justify-between gap-3">
                  <div className="h-2.5 w-40 rounded bg-panel-2" />
                  <div className="h-8 w-28 rounded-full bg-terracotta" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Chip>Ottimizzato mobile</Chip>
              <Chip>Caricamento &lt; 1s</Chip>
              <Chip>Copywriting incluso</Chip>
            </div>
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:col-span-5">
            <Card delay={0.08} testid="service-card-booking">
              <Head
                n="02"
                icon={CalendarCheck}
                title="Sistema Prenotazioni Direct"
                desc="Modulo di prenotazione tavolo o camera integrato nel sito: il cliente prenota da te, non dalla piattaforma."
              />
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <Chip>Zero provvigioni OTA</Chip>
                <Chip>Conferma immediata</Chip>
                <DemoLink to="/demo" testid="service-demo-booking" />
              </div>
            </Card>

            <Card delay={0.14} testid="service-card-menu">
              <Head
                n="03"
                icon={QrCode}
                title="Menu Digitale & QR Smart"
                desc="Menu interattivo per ristoranti e guida ospiti per B&B: sempre aggiornato, in tante lingue, senza costi di stampa."
              />
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <Chip>Multilingua</Chip>
                <Chip>Aggiornabile da solo</Chip>
                <DemoLink to="/demo?tab=menu" testid="service-demo-menu" />
              </div>
            </Card>
          </div>

          <Card className="lg:col-span-6" delay={0.08} testid="service-card-seo">
            <Head
              n="04"
              icon={MapPin}
              title="SEO Locale & Google Maps"
              desc="Dominio sulle ricerche geolocalizzate per farti trovare proprio quando qualcuno cerca «ristorante vicino a me»."
            />
            <div className="mt-6 flex flex-wrap gap-2">
              <Chip>Google Business Profile</Chip>
              <Chip>Recensioni</Chip>
              <Chip>Local pack</Chip>
            </div>
          </Card>

          <Card className="lg:col-span-6" delay={0.14} testid="service-card-story">
            <Head
              n="05"
              icon={Camera}
              title="Storytelling Fotografico"
              desc="Direzione artistica e gallerie ad alta risoluzione: l'atmosfera del tuo locale prima ancora che il cliente entri."
            />
            <div className="mt-6 flex flex-wrap gap-2">
              <Chip>Direzione artistica</Chip>
              <Chip>Gallerie HD</Chip>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}